import { MIN_MARGIN_AMOUNT } from '@kwenta/sdk/constants'
import { PerpsProvider } from '@kwenta/sdk/types'
import { wei } from '@kwenta/wei'
import { memo, useMemo } from 'react'
import styled from 'styled-components'

import Spacer from 'components/Spacer'
import {
	selectMarketAsset,
	selectPerpsProvider,
	selectTradeOrderType,
} from 'state/futures/common/selectors'
import {
	selectIsolatedAvailableMargin,
	selectSnxV2Account,
	selectSnxV2LockedMarginInMarkets,
} from 'state/futures/isolatedMargin/selectors'
import {
	selectIsFetchingTradePreview,
	selectPlaceOrderDisabledReason,
	selectTradePreview,
} from 'state/futures/selectors'
import { selectSnxV3Account } from 'state/futures/snxPerpsV3/selectors'
import { useAppSelector } from 'state/hooks'
import { providerIsCrossMargin } from 'utils/futures'

import CrossMarginTradeFees from '../FeeInfoBox/CrossMarginTradeFees'
import SmartMarginTradeFees from '../FeeInfoBox/SmartMarginTradeFees'

import { FlexDivCol } from 'components/layout/flex'
import isEqual from 'lodash/isEqual'
import OneClickTradingSetupMessage from './OneClickTradingSetupMessage'
import { FillPriceRow, LiquidationRow, PriceImpactRow, SlippageRow } from './PreviewRows'

const TradePanelPreview = memo(() => {
	const preview = useAppSelector(selectTradePreview)
	const asset = useAppSelector(selectMarketAsset)
	const accountMargin = useAppSelector(selectIsolatedAvailableMargin)
	const snxV2LockedMargin = useAppSelector(selectSnxV2LockedMarginInMarkets)
	const placeOrderDisabledReason = useAppSelector(selectPlaceOrderDisabledReason, isEqual)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const smAccount = useAppSelector(selectSnxV2Account)
	const cmAccount = useAppSelector(selectSnxV3Account)
	const isFetchingPreview = useAppSelector(selectIsFetchingTradePreview)
	const orderType = useAppSelector(selectTradeOrderType)

	const isIsolated = !providerIsCrossMargin(perpsProvider)

	const shouldShowData = useMemo(() => {
		if (isFetchingPreview) {
			return false
		}

		if (perpsProvider === PerpsProvider.SNX_V2_OP) {
			// Unrestricted preview without an account
			if (!smAccount) return true
			const isDepositRequired =
				wei(accountMargin).lt(MIN_MARGIN_AMOUNT) && wei(snxV2LockedMargin).eq(0)
			return !placeOrderDisabledReason && !isDepositRequired && !!smAccount
		} else if (perpsProvider === PerpsProvider.SNX_V3_BASE) {
			// Unrestricted preview without an account
			if (!cmAccount) return true
			return !placeOrderDisabledReason
		}
		return true
	}, [
		smAccount,
		accountMargin,
		snxV2LockedMargin,
		placeOrderDisabledReason,
		perpsProvider,
		cmAccount,
		isFetchingPreview,
	])

	return (
		<FeeInfoBoxContainer rowGap="16px">
			<OneClickTradingSetupMessage />
			<FlexDivCol>
				{isIsolated ? (
					<SmartMarginTradeFees showData={shouldShowData} />
				) : (
					<CrossMarginTradeFees showData={shouldShowData} />
				)}
				<FillPriceRow
					showData={shouldShowData}
					fillPrice={preview?.fillPrice ? wei(preview.fillPrice) : undefined}
					asset={asset}
				/>
				<PriceImpactRow
					showData={shouldShowData}
					priceImpact={preview?.priceImpact ? wei(preview.priceImpact) : undefined}
				/>
				<SlippageRow orderType={orderType} />
				<Divider height={1} divider />
				<LiquidationRow showData={shouldShowData} liqPrice={preview?.liqPrice} />
			</FlexDivCol>
		</FeeInfoBoxContainer>
	)
})

const FeeInfoBoxContainer = styled(FlexDivCol)``

const Divider = styled(Spacer)`
	margin: 15px 0;
	width: 100%;
`

export default TradePanelPreview
